import { faBook, faCircleExclamation, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, type ColumnType, IconButton, Modal, ModalCloseButton, ModalDescription, ModalFooter, ModalHeader, ModularTable, NotificationDispatch, type RowType, SortEnum, TableColorThemeEnum, TextField } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import { useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { StyledTabHead } from 'src/features/admin/cloud-training/aws-cloud-training.styles';
import { StyledModalTitle } from 'src/features/admin/corporate/components/add-seat-modal/add-seat-modal.styles';
import { StyledAddIcon, StyledFontAwesomeIcon, StyledHeading, StyledNoRecordMessage } from 'src/features/admin/corporate/corporate.styles';
import type { CompanyItem } from 'src/features/admin/corporate/corporate.types';
import { useAddPathToCompanyMutation, useRemovePathFromCompanyMutation } from '../../corporate.slice';
interface PathsRowType extends RowType {
  _id: string;
  code: string;
}
export const PathsTable = ({
  paths,
  companyId
}: {
  paths: CompanyItem['paths'];
  companyId: string;
}) => {
  const [isRemovePathModalOpen, setRemovePathModalOpen] = useState(false);
  const [isAddPathModalOpen, setAddPathModalOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState<{
    code: string;
    _id: string;
  } | null>(null);
  const [addPathToCompany] = useAddPathToCompanyMutation();
  const [removePathFromCompany] = useRemovePathFromCompanyMutation();
  const handleRemoveExistingPath = async (data: {
    code: string;
  }) => {
    try {
      await removePathFromCompany({
        companyId,
        code: data.code
      }).unwrap();
      NotificationDispatch('success', 'Path removed successfully');
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Failed to remove path. Please try again.');
    }
  };
  const {
    register,
    handleSubmit,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      code: ''
    }
  });
  const columns: ColumnType<PathsRowType>[] = [{
    key: 'code',
    title: 'Path Code',
    flex: '0 0 45%'
  }, {
    key: '_id',
    title: 'Path ID',
    flex: '0 0 45%'
  }, {
    key: 'actions',
    title: 'Actions',
    justifyContent: 'center',
    render: (_, row: PathsRowType) => <IconButton variant="delete" width="4rem" height="4rem" aria-label="action" onClick={() => {
      setSelectedPath({
        code: row.code,
        _id: row._id
      });
      setRemovePathModalOpen(true);
    }}>
          <FontAwesomeIcon icon={faTrashCan} size="lg" />
        </IconButton>
  }];
  const rows: PathsRowType[] = (paths || []).map(path => ({
    code: path.code,
    _id: path._id,
    id: path._id
  }));
  const handleRemovePath = () => {
    if (!selectedPath) return;
    setRemovePathModalOpen(false);
    handleRemoveExistingPath({
      code: selectedPath.code
    });
  };
  const handleAddPath = async (data: {
    code: string;
  }) => {
    try {
      await addPathToCompany({
        companyId,
        code: data.code
      }).unwrap();
      NotificationDispatch('success', 'Path added successfully');
      reset();
      setAddPathModalOpen(false);
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Failed to add path. Please check if the path code exists.');
    }
  };
  return <>
      <Modal open={isRemovePathModalOpen} onOpenChange={setRemovePathModalOpen} isSmall data-sentry-element="Modal" data-sentry-source-file="paths-table.tsx">
        <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="paths-table.tsx">
          <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="paths-table.tsx">
            <FontAwesomeIcon icon={faCircleExclamation} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="paths-table.tsx" />
            Remove path
          </StyledModalTitle>
        </ModalHeader>
        <ModalDescription data-sentry-element="ModalDescription" data-sentry-source-file="paths-table.tsx">Are you sure you want to remove this path?</ModalDescription>
        <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="paths-table.tsx">
          <ModalCloseButton text="Cancel" data-sentry-element="ModalCloseButton" data-sentry-source-file="paths-table.tsx" />
          <Button color="error" variant="solid" onClick={handleRemovePath} data-sentry-element="Button" data-sentry-source-file="paths-table.tsx">
            Remove path
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={isAddPathModalOpen} onOpenChange={setAddPathModalOpen} isSmall data-sentry-element="Modal" data-sentry-source-file="paths-table.tsx">
        <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="paths-table.tsx">
          <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="paths-table.tsx">
            <FontAwesomeIcon icon={faPlus} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="paths-table.tsx" />
            Add new path
          </StyledModalTitle>
        </ModalHeader>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(handleAddPath)}>
          <ModalDescription data-sentry-element="ModalDescription" data-sentry-source-file="paths-table.tsx">
            <TextField {...register('code')} label="Path code" placeholder="Enter path code" variant="input" isRequired data-sentry-element="TextField" data-sentry-source-file="paths-table.tsx" />
          </ModalDescription>
          <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="paths-table.tsx">
            <ModalCloseButton text="Cancel" data-sentry-element="ModalCloseButton" data-sentry-source-file="paths-table.tsx" />
            <Button color="primary" variant="solid" type="submit" data-sentry-element="Button" data-sentry-source-file="paths-table.tsx">
              Add path
            </Button>
          </ModalFooter>
        </form>
      </Modal>
      <StyledTabHead style={{
      justifyContent: 'space-between',
      display: 'flex',
      alignItems: 'center'
    }} data-sentry-element="StyledTabHead" data-sentry-source-file="paths-table.tsx">
        <div>
          <StyledFontAwesomeIcon icon={faBook} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="paths-table.tsx" />
          <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="paths-table.tsx">Assigned Paths</StyledHeading>
        </div>
        <Button variant="outlined" color="secondary" onClick={() => setAddPathModalOpen(true)} data-sentry-element="Button" data-sentry-source-file="paths-table.tsx">
          <StyledAddIcon icon={faPlus} data-sentry-element="StyledAddIcon" data-sentry-source-file="paths-table.tsx" />
          Add path
        </Button>
      </StyledTabHead>
      <ModularTable<PathsRowType> columns={columns} rows={rows} colorTheme={TableColorThemeEnum.grayWhite} activeSortColumn={null} activeSortOrder={SortEnum.ascending} data-sentry-element="ModularTable" data-sentry-source-file="paths-table.tsx" />
      {!paths.length && <StyledNoRecordMessage>No paths found</StyledNoRecordMessage>}
    </>;
};